import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { FileText, Loader2, RefreshCw, Plus, Check, Trash2, X, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall, getApiBase } from '@/lib/api';

interface WaiverDoc {
  id: string;
  title: string;
  body_md: string;
  version: number;
  is_active: number;
  created_at: string;
  updated_at: string;
  signature_count: number;
}

interface SignatureRow {
  id: string;
  waiver_id: string;
  member_id: string | null;
  signer_name: string;
  signer_email: string | null;
  signed_at: string;
  ip_address: string | null;
  waiver_title: string;
  member_name: string | null;
}

const DEFAULT_BODY = `# Liability waiver

By signing below, I acknowledge that participation in physical training,
sparring, and related activities at United Wrestling Club carries inherent
risks of injury. I assume those risks knowingly and voluntarily.

I release United Wrestling Club, its coaches, staff, and members from
liability for any injury, loss, or damage that may result from my
participation.

I confirm that I am physically able to participate in these activities or
have consulted a physician regarding the same.
`;

export default function Waivers() {
  const { getToken } = useAuth();
  const [docs, setDocs] = useState<WaiverDoc[]>([]);
  const [sigs, setSigs] = useState<SignatureRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<WaiverDoc | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const [d, s] = await Promise.all([
        apiCall<{ items: WaiverDoc[] }>('/v1/waivers', { bearerToken: token }),
        apiCall<{ items: SignatureRow[] }>('/v1/waivers/signatures', { bearerToken: token, query: { limit: 100 } }),
      ]);
      setDocs(d.items);
      setSigs(s.items);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function activate(id: string) {
    const token = await getToken();
    await apiCall(`/v1/waivers/${id}`, { method: 'PATCH', bearerToken: token, body: { is_active: true } });
    void load();
  }

  async function remove(id: string) {
    const token = await getToken();
    await apiCall(`/v1/waivers/${id}`, { method: 'DELETE', bearerToken: token });
    void load();
  }

  async function removeSig(id: string) {
    const token = await getToken();
    await apiCall(`/v1/waivers/signatures/${id}`, { method: 'DELETE', bearerToken: token });
    setSigs((prev) => prev.filter((x) => x.id !== id));
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Waivers</h1>
          <p className="text-sm text-muted-foreground">
            Liability waivers. Public signing page at{' '}
            <a href="/waiver" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">
              /waiver <ExternalLink className="h-3 w-3" />
            </a>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" /> New waiver
          </Button>
        </div>
      </div>

      {err && <Card><CardContent className="py-4 text-sm text-destructive">{err}</CardContent></Card>}

      {docs.length === 0 && !loading && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          <FileText className="mx-auto mb-3 h-8 w-8 opacity-40" />
          No waivers yet. <button className="underline" onClick={() => setShowCreate(true)}>Create one.</button>
        </CardContent></Card>
      )}

      {docs.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y">
              {docs.map((d) => (
                <li key={d.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{d.title}</span>
                      {d.is_active ? <Badge>Active</Badge> : <Badge variant="outline">Inactive</Badge>}
                      <span className="text-xs text-muted-foreground">v{d.version} · {d.signature_count} signature{d.signature_count === 1 ? '' : 's'}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-1">
                      {d.body_md.slice(0, 200)}…
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => setEditing(d)}>Edit</Button>
                    {!d.is_active && (
                      <Button variant="outline" size="sm" onClick={() => void activate(d.id)}>
                        <Check className="h-3.5 w-3.5 mr-1" /> Activate
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => void remove(d.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {sigs.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-2">Signatures</h2>
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="px-3 py-2 font-medium">Signer</th>
                    <th className="px-3 py-2 font-medium">Member</th>
                    <th className="px-3 py-2 font-medium">Waiver</th>
                    <th className="px-3 py-2 font-medium">Signed</th>
                    <th className="px-3 py-2 font-medium">Signature</th>
                    <th className="px-3 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sigs.map((s) => (
                    <tr key={s.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="px-3 py-2">
                        <div className="font-medium">{s.signer_name}</div>
                        {s.signer_email && <div className="text-xs text-muted-foreground">{s.signer_email}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">{s.member_name ?? <span className="text-muted-foreground">guest</span>}</td>
                      <td className="px-3 py-2 text-xs">{s.waiver_title}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground" title={s.signed_at}>{s.signed_at.replace(' ', ' at ')}</td>
                      <td className="px-3 py-2">
                        <img
                          src={`${getApiBase()}/v1/waivers/signatures/${s.id}/image`}
                          alt="signature"
                          className="h-10 w-auto rounded border bg-white"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => void removeSig(s.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {(showCreate || editing) && (
        <WaiverEditor
          existing={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); void load(); }}
          defaultBody={editing ? editing.body_md : DEFAULT_BODY}
        />
      )}
    </div>
  );
}

function WaiverEditor(props: { existing: WaiverDoc | null; defaultBody: string; onClose: () => void; onSaved: () => void }) {
  const { getToken } = useAuth();
  const [title, setTitle] = useState(props.existing?.title ?? 'Liability waiver');
  const [body, setBody] = useState(props.defaultBody);
  const [makeActive, setMakeActive] = useState(props.existing?.is_active === 1 || !props.existing);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const token = await getToken();
      if (props.existing) {
        await apiCall(`/v1/waivers/${props.existing.id}`, {
          method: 'PATCH', bearerToken: token,
          body: { title, body_md: body, is_active: makeActive },
        });
      } else {
        await apiCall('/v1/waivers', {
          method: 'POST', bearerToken: token,
          body: { title, body_md: body, is_active: makeActive },
        });
      }
      props.onSaved();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setSubmitting(false); }
  }

  const inp = 'w-full rounded-md border bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={props.onClose}>
      <div className="bg-card text-card-foreground rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h2 className="text-base font-semibold">{props.existing ? 'Edit waiver' : 'New waiver'}</h2>
          <button onClick={props.onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-3 overflow-auto flex-1">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required className={inp} />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Body (Markdown)</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={14}
              className={inp + ' font-mono text-xs resize-y min-h-[200px]'} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={makeActive} onChange={(e) => setMakeActive(e.target.checked)} />
            Make this the active waiver (everyone signs this version going forward)
          </label>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="ghost" onClick={props.onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting || !title.trim()}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
